import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useToasts } from 'react-toast-notifications';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Button from 'react-bootstrap/Button';
import {
    subscriptionPrice as subscriptionPriceRules,
} from '../utils/rules';
import BCStep1 from '../images/BC-Step-1.png';
import PConfettiModal from './PConfettiModal';
import { HashLink } from 'react-router-hash-link';
import { Alert, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { connect } from 'react-redux';
import { ROLES } from '../utils/constants';

const SubscriptionPrice = ({
    user,
    showHeader,
    getUserInformation
}) => {
    const { addToast } = useToasts();
    const history = useHistory();

    const [showPanelHeader, setShowPanelHeader] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const [busy, setBusy] = useState(false);
    const [minSubscriptionPrice, setMinSubscriptionPrice] = useState(subscriptionPriceRules.minAmount);
    const [maxSubscriptionPrice, setMaxSubscriptionPrice] = useState(subscriptionPriceRules.maxAmount);
    const [idSubmitted, setIdSubmitted] = useState(false);

    useEffect(() => {
        if (user && Object.keys(user).length > 0 && user.creator && Object.keys(user.creator).length > 0) {
            formik.setFieldValue('subscription_price', user.creator.subscription_price);
        }

        if (user && user.creator && Object.keys(user.creator).length > 0) {
            if (user.creator.id_submitted) {
                setIdSubmitted(true);
            }

            formik.setFieldValue('free_subscription', user.creator.free_subscription);
        }
    }, [user]);

    const getSettings = async () => {
        setBusy(true);
        await axios.get('/api/subscription-settings')
            .then(response => {
                setMinSubscriptionPrice(response.data.min_subscription_price);
                setMaxSubscriptionPrice(response.data.max_subscription_price);
                setBusy(false);
            })
            .catch(error => {
                console.log({ error });
                setBusy(false);
            });
    };

    const clearAllErrors = () => {
        Object.keys(formik.errors).forEach(field => {
            formik.setFieldError(field, '');
        });
    };

    const formik = useFormik({
        initialValues: {
            subscription_price: 0,
            free_subscription: false
        },
        validationSchema: Yup.object({
            subscription_price: Yup.lazy(value => {
                return Yup.number()
                    .required(`The maximum subscription price for creator accounts is $${maxSubscriptionPrice} USD, and minimum is $${minSubscriptionPrice} USD`)
                    .typeError('Invalid subscription price')
                    .moreThan(minSubscriptionPrice - 0.01, `The maximum subscription price for creator accounts is $${maxSubscriptionPrice} USD, and minimum is $${minSubscriptionPrice} USD`)
                    .lessThan(maxSubscriptionPrice + 0.01, `The maximum subscription price for creator accounts is $${maxSubscriptionPrice} USD, and minimum is $${minSubscriptionPrice} USD`);
            })
        }),
        onSubmit: async (values) => {
            clearAllErrors();
            setIsLoading(true);
            formik.errors['subscription_price'] = '';
            await axios.patch(`/api/users/${localStorage.getItem('loggedin_user')}`, { ...values })
                .then((response) => {
                    const userFromLocalStorage = JSON.parse(localStorage.getItem('user'));
                    getUserInformation();
                    if (response.data.user.role != userFromLocalStorage.role) {
                        setShowConfetti(true);
                        localStorage.setItem(JSON.stringify(response.data.user));
                    } else {
                        addToast('Subscription price successfully updated', {
                            appearance: 'success',
                            autoDismiss: true,
                        });
                    }

                    setIsLoading(false);
                    setShow(true);
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.response.status === 422) {
                        Object.keys(error.response.data.errors).forEach((key) => {
                            if (key !== 'code') {
                                formik.errors[key] = error.response.data.errors[key][0];
                            }
                        });
                    }
                    addToast('Unable to update subscription price', {
                        appearance: 'error',
                        autoDismiss: true,
                    });

                    setIsLoading(false);
                    setShow(true);
                });
        },
    });

    useEffect(() => {
        if (typeof showHeader === 'undefined') {
            setShowPanelHeader(true);
        } else {
            setShowPanelHeader(showHeader);
        }

        getSettings();
    }, []);

    const handleSubscriptionPrice = (e) => {
        const value = e.target.value.replace(/[^0-9.]/g, '');
        formik.setFieldValue('subscription_price', value.replace(/\.{2,}/g, '.'), true);
        clearAllErrors();
    };

    const handleFreeSubscriptionChange = () => {
        formik.setFieldValue('free_subscription', !formik.values.free_subscription);
    };

    return (
        <div className="panel panel-default">
            {showPanelHeader && (
                <div className="panel-heading refpayout">
                    <span className="BC-head-ico">
                        <LazyLoadImage src={BCStep1} />
                    </span>
                    <span>
                        Step 2 - Subscription Plans ($)*
                    </span>
                </div>
            )}
            <div className="panel-body">
                {/* {
                    !idSubmitted
                        ? <div className="subscription-note">
                            <Alert severity='info'>
                                You must first <HashLink smooth to="/become-creator#step1">submit your ID</HashLink> for verification to enter a Subscription price and become a Creator.
                            </Alert>
                        </div>
                        : null
                } */}
                <form
                    onSubmit={(e) => e.preventDefault()}
                    className=""
                    id="subscription_payment_form"
                    name="subscription_payment_form"
                    method="post"
                    action=""
                    encType="multipart/form-data"
                    noValidate="novalidate"
                >
                    <div className="become-creator-subscription">
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <label>
                                Paid Subscription ($)
                                <span className="required">*</span>
                            </label>
                            <div className="subscription-price-fields mt-3 mb-4">
                                <input
                                    type="text"
                                    placeholder="0.00"
                                    id="subscription_price"
                                    className={`form-control${formik.errors.subscription_price && formik.touched.subscription_price ? ' is-invalid' : ''}`}
                                    onChange={handleSubscriptionPrice}
                                    value={formik.values.subscription_price}
                                />
                                <span>USD per month</span>
                            </div>
                        </div>
                        <div style={{ margin: '12px auto', justifyContent: 'left', display: 'flex' }}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formik.values.free_subscription}
                                            onChange={handleFreeSubscriptionChange}
                                        />
                                    }
                                    label="Free Following Plan Option"
                                    labelPlacement='start'
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontWeight: 'bold'
                                        }
                                    }}
                                />
                            </FormGroup>
                        </div>
                        <div>
                            <p>
                                When the Free Following option is enabled, your "Free Post" types will be viewable when followers choose this plan. "Paid Post" types will be locked to Free Followers which need a paid subscription to view.
                            </p>
                        </div>
                        <div style={{ marginTop: '24px' }}>
                            <Button
                                type="button"
                                disabled={isLoading || busy}
                                className="btn theme-btn theme-btn-bordered submit-id-btn"
                                onClick={formik.handleSubmit}
                            >
                                {isLoading ? 'Processing' : 'Save'}
                            </Button>
                        </div>

                        {
                            formik.errors.subscription_price && formik.touched.subscription_price
                                ? <div style={{ color: 'red', marginBottom: '12px' }}>{formik.errors.subscription_price}</div>
                                : null
                        }


                    </div>
                </form>
            </div>

            {
                idSubmitted && user.role == ROLES.PU
                    ? <>
                        <hr />

                        <div style={{ margin: '24px auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>
                                <Link to='/promotion'>Click here for Promotions and Discount Links Setup</Link>
                            </p>
                        </div>
                    </>
                    : null
            }

            {showConfetti && <PConfettiModal show={showConfetti} setShow={setShowConfetti} />}
        </div>
    );
};

const mapStateToProps = state => ({
    user: state.user.user
});

export default connect(mapStateToProps)(SubscriptionPrice);
